import React from 'react';

const selectStyle = {
    background: 'rgba(30,41,59,0.8)',
    border: '1px solid rgba(148,163,184,0.2)',
    borderRadius: '6px',
    color: '#e2e8f0',
    padding: '4px 8px',
    fontSize: '12px',
    minWidth: '160px',
};

/**
 * Reusable duplicate warning table component.
 *
 * @param {Object} props
 * @param {Array}    props.duplicates          - Array of duplicate entries, each with:
 *                                               { leadId, email, instagram, name, ownerName, statusLabel,
 *                                                 statusSince, isRefused, isClaimable, isFree, isAmbassador,
 *                                                 daysInStatus, team, scout, row?, claimed? }
 * @param {Set}      props.claimIds            - Set of leadIds selected for claiming
 * @param {Function} props.onToggleClaim       - (leadId) => void
 * @param {Function} props.onToggleAllClaims   - () => void
 * @param {Function} props.onClaim             - () => void — triggers claim action
 * @param {boolean}  props.claiming            - Loading state during claim
 * @param {Array}    [props.campaigns]         - Optional campaign list for claim campaign selector
 * @param {string}   [props.claimCampaignId]   - Selected campaign ID
 * @param {Function} [props.onClaimCampaignChange] - (e) => void
 * @param {boolean}  [props.showRowColumn]     - Show row # column (for CSV import)
 */
export default function DuplicateWarning({
    duplicates,
    claimIds,
    onToggleClaim,
    onToggleAllClaims,
    onClaim,
    claiming,
    campaigns = [],
    claimCampaignId = '',
    onClaimCampaignChange,
    showRowColumn = false,
    compact = false,
}) {
    if (!duplicates || duplicates.length === 0) return null;

    const unclaimedDuplicates = duplicates.filter(d => !d.claimed && d.isClaimable);

    if (compact) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {duplicates.map((d, i) => {
                    const field = d.field === 'instagram' ? 'IG' : d.field === 'tiktok' ? 'TT' : d.field === 'email' ? 'EM' : '';
                    const handle = d.instagram || d.email || d.originalName || '—';
                    const parts = [field, handle, d.scout || d.ownerName, d.statusLabel].filter(Boolean);

                    let badge = null;
                    if (d.isAmbassador) {
                        badge = <span style={{ padding: '1px 6px', borderRadius: 4, background: '#FEE2E2', color: '#DC2626', fontSize: '11px', whiteSpace: 'nowrap' }}>🚫 Амбассадор</span>;
                    } else if (d.isFree) {
                        badge = <span style={{ padding: '1px 6px', borderRadius: 4, background: '#D1FAE5', color: '#059669', fontSize: '11px', whiteSpace: 'nowrap' }}>✅ Свободен · {d.daysInStatus} дн.</span>;
                    } else {
                        badge = <span style={{ padding: '1px 6px', borderRadius: 4, background: '#FEF3C7', color: '#D97706', fontSize: '11px', whiteSpace: 'nowrap' }}>🔒 Активен · {d.daysInStatus} дн.</span>;
                    }

                    const isOriginal = !d.isDuplicate;
                    const roleIndicator = isOriginal ? '🟢' : '🟡';

                    return (
                        <div key={d.leadId || i} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--neutral-80)', flexWrap: 'wrap' }}>
                            <span title={isOriginal ? 'Оригинал' : 'Дубль'}>{roleIndicator}</span>
                            <span>{parts.join(' • ')}</span>
                            {badge}
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <div style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                <h4 style={{ margin: 0, fontSize: '14px', color: '#e2e8f0' }}>
                    Найденные дубликаты
                </h4>
                {unclaimedDuplicates.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {campaigns.length > 0 && (
                            <select
                                value={claimCampaignId}
                                onChange={onClaimCampaignChange}
                                style={selectStyle}
                            >
                                <option value=''>Без кампании</option>
                                {campaigns.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        )}
                        <button
                            className='primary-button'
                            onClick={onClaim}
                            disabled={claiming || claimIds.size === 0}
                            style={{ fontSize: '12px', padding: '4px 12px' }}
                        >
                            {claiming ? '...' : `Забрать себе (${claimIds.size})`}
                        </button>
                    </div>
                )}
            </div>
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                <table className='data-table' style={{ width: '100%', fontSize: '12px' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '30px' }}>
                                <input
                                    type='checkbox'
                                    checked={claimIds.size > 0 && claimIds.size === unclaimedDuplicates.length}
                                    onChange={onToggleAllClaims}
                                    disabled={unclaimedDuplicates.length === 0}
                                />
                            </th>
                            {showRowColumn && <th>#</th>}
                            <th>Email / Instagram</th>
                            <th>Имя</th>
                            <th>Менеджер</th>
                            <th>Статус</th>
                            <th>Инфо</th>
                        </tr>
                    </thead>
                    <tbody>
                        {duplicates.map((d, i) => (
                            <tr key={d.leadId || i} style={d.claimed ? { opacity: 0.4 } : {}}>
                                <td>
                                    {d.claimed ? (
                                        <span style={{ color: '#22c55e' }}>✓</span>
                                    ) : (
                                        <input
                                            type='checkbox'
                                            checked={claimIds.has(d.leadId)}
                                            onChange={() => onToggleClaim(d.leadId)}
                                            disabled={!d.isClaimable}
                                        />
                                    )}
                                </td>
                                {showRowColumn && <td>{d.row}</td>}
                                <td>{d.email || d.instagram || '—'}</td>
                                <td>
                                    <div>{d.name || '—'}</div>
                                    {d.team && (
                                        <div style={{ color: 'rgba(148,163,184,0.6)', fontSize: '10px' }}>{d.team}</div>
                                    )}
                                </td>
                                <td>
                                    {d.ownerName || d.scout || '—'}
                                </td>
                                <td>
                                    <span style={{
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        background: d.isRefused ? '#FEE2E2' : '#F0F0F5',
                                        fontSize: '11px',
                                        color: d.isRefused ? '#DC2626' : '#6B7280',
                                    }}>
                                        {d.statusLabel || d.statusId || '—'}
                                    </span>
                                    {d.metadata?.duplicate_check_air && (
                                        <div style={{ color: 'rgba(148,163,184,0.5)', fontSize: '10px', marginTop: '2px' }}>
                                            {d.metadata.duplicate_check_air}
                                        </div>
                                    )}
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                    {d.isAmbassador ? (
                                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '4px', background: '#FEE2E2', color: '#DC2626', fontSize: '11px', whiteSpace: 'nowrap' }}>
                                            🚫 Запрещено, лид активный амбассадор
                                        </span>
                                    ) : d.isFree ? (
                                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '4px', background: '#D1FAE5', color: '#059669', fontSize: '11px', whiteSpace: 'nowrap' }}>
                                            ✅ Лид свободен · {d.daysInStatus} дн. назад
                                        </span>
                                    ) : (
                                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '4px', background: '#FEF3C7', color: '#D97706', fontSize: '11px', whiteSpace: 'nowrap' }}>
                                            🔒 Активен · {d.daysInStatus} дн. назад
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
